import type {
  AgingBalanceMonthly,
  AgingBalanceSnapshot,
  AnalyticsInterval,
  CashCollectedMonthly,
  CollectionsSummary,
  CollectionsSummaryLine,
  DsoMonthly,
  OutreachSummary,
  OutreachSummaryLine,
} from '@api/models';
import { defineStore } from 'pinia';

import type { InvoiceBalanceSummaryResponse } from '@stuut/api-sdk/models';
import { waitFor } from '@stuut/common/src/utils';

import { stuut } from '@/api';
import { sdk } from '@/sdk';
import { downloadCSV } from '@/utils/csvDownload';

interface State {
  agingBalanceMonthly?: AgingBalanceMonthly | null;
  agingBalanceSnapshot?: AgingBalanceSnapshot | null;
  cashCollectedMonthly?: CashCollectedMonthly | null;
  collectionIntervalSummary: Record<AnalyticsInterval, CollectionsSummaryLine[] | null>;
  collectionIntervalSummaryLoading: boolean;
  collectionsSummary?: CollectionsSummary | null;
  dsoMonthly?: DsoMonthly | null;
  invoiceBalanceSummary?: InvoiceBalanceSummaryResponse | null;
  outreachIntervalSummary: Record<AnalyticsInterval, OutreachSummaryLine[] | null>;
  outreachIntervalSummaryLoading: boolean;
  outreachSummary?: OutreachSummary | null;
}

export const useAnalyticsStore = defineStore('analytics', {
  actions: {
    async downloadAgingBalanceCsv() {
      try {
        const { data } = await stuut.analytics.getReport('aging-balance-by-month', true, false);
        downloadCSV(data, 'aging_balance_by_month.csv');
      } catch (error) {
        console.error(error);
      }
    },

    async downloadAgingBalanceSnapshotCsv() {
      try {
        const { data } = await stuut.analytics.getReport('aging-balance', true, false);
        downloadCSV(data, 'aging_balance.csv');
      } catch (error) {
        console.error(error);
      }
    },

    // async fetchAgingBalance(): Promise<any> {
    //   try {
    //     const agingBalance = await stuut.analytics.getReport(
    //       'aging-balance-by-month',
    //     );

    //     this.$patch({ agingBalanceMonthly: agingBalance });

    //     return this.$state?.agingBalanceMonthly;
    //   } catch (error) {
    //     console.error(error);
    //   }
    // },

    async downloadInvoiceSummaryStatCsv() {
      try {
        const { data } = await stuut.analytics.getReport('invoice-balance-summary', true, false);
        downloadCSV(data, 'invoice_summary_stats.csv');
      } catch (error) {
        console.error(error);
      }
    },

    async fetchAgingBalance(): Promise<any> {
      try {
        const agingBalance = await stuut.analytics.getReport('historical-aging-balance-by-month');

        this.$patch({ agingBalanceMonthly: agingBalance });

        return this.$state?.agingBalanceMonthly;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchAgingBalanceSnapshot(): Promise<any> {
      try {
        const agingBalanceSnapshot = await stuut.analytics.getReport('aging-balance');

        this.$patch({ agingBalanceSnapshot });

        return this.$state?.agingBalanceSnapshot;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchCashCollected(): Promise<any> {
      try {
        const cashCollectedMonthly = await stuut.analytics.getReport('cash-collected-by-month');

        this.$patch({ cashCollectedMonthly });

        return this.$state?.cashCollectedMonthly;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchCollections(): Promise<any> {
      try {
        const collectionsSummary = await stuut.analytics.getReport('collections-summary');

        this.$patch({ collectionsSummary });

        return this.$state?.collectionsSummary;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchCollectionsIntervalSummary(
      interval: AnalyticsInterval,
    ): Promise<CollectionsSummaryLine[]> {
      if (this.$state.collectionIntervalSummary[interval]) {
        return this.$state.collectionIntervalSummary[interval] || [];
      }

      if (this.$state.collectionIntervalSummaryLoading) {
        await waitFor(() => !this.$state.collectionIntervalSummaryLoading, 100);
      } else {
        this.$patch({ collectionIntervalSummaryLoading: true });
        try {
          const res = await stuut.analytics.getCollectionsSummary(interval);
          this.$patch({
            collectionIntervalSummary: {
              ...this.$state.collectionIntervalSummary,
              [interval]: res,
            },
            collectionIntervalSummaryLoading: false,
          });
        } catch (error) {
          this.$patch({ collectionIntervalSummaryLoading: false });
          throw error;
        }
      }

      return this.$state.collectionIntervalSummary[interval] || [];
    },

    async fetchDsoMonthly(): Promise<any> {
      try {
        const dsoMonthly = await stuut.analytics.getReport('dso-by-month');

        this.$patch({ dsoMonthly });

        return this.$state?.dsoMonthly;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchInvoiceSummaryStat(): Promise<any> {
      try {
        const invoiceSummaryStat = await sdk.analytics.getInvoiceBalanceSummary();

        this.$patch({ invoiceBalanceSummary: invoiceSummaryStat });

        return this.$state?.invoiceBalanceSummary;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchOutreach(): Promise<any> {
      try {
        const outreachSummary = await stuut.analytics.getReport('outreach-summary');

        this.$patch({ outreachSummary });

        return this.$state?.outreachSummary;
      } catch (error) {
        console.error(error);
      }
    },

    async fetchOutreachIntervalSummary(
      interval: AnalyticsInterval,
    ): Promise<OutreachSummaryLine[]> {
      if (this.$state.outreachIntervalSummary[interval]) {
        return this.$state.outreachIntervalSummary[interval] || [];
      }

      if (this.$state.outreachIntervalSummaryLoading) {
        await waitFor(() => !this.$state.outreachIntervalSummaryLoading, 250);
      } else {
        this.$patch({ outreachIntervalSummaryLoading: true });
        try {
          const res = await stuut.analytics.getOutreachSummary(interval);
          this.$patch({
            outreachIntervalSummary: {
              ...this.$state.outreachIntervalSummary,
              [interval]: res,
            },
            outreachIntervalSummaryLoading: false,
          });
        } catch (error) {
          this.$patch({ outreachIntervalSummaryLoading: false });
          throw error;
        }
      }

      return this.$state.outreachIntervalSummary[interval] || [];
    },
  },

  getters: {
    summaryDue(state): number {
      return state.invoiceBalanceSummary?.due?.amount || 0;
    },

    summaryOutstanding(): number {
      return this.summaryDue + this.summaryOverdue;
    },

    summaryOverdue(state): number {
      return state.invoiceBalanceSummary?.overdue?.amount || 0;
    },

    summaryUnapplied(): number {
      return 0;
    },
  },

  state: () =>
    ({
      agingBalanceMonthly: null,
      agingBalanceSnapshot: null,
      cashCollectedMonthly: null,
      collectionIntervalSummary: {
        all: null,
        monthly: null,
        weekly: null,
      },
      collectionIntervalSummaryLoading: false,
      collectionsSummary: null,
      dsoMonthly: null,
      invoiceBalanceSummary: null,
      outreachIntervalSummary: {
        all: null,
        monthly: null,
        weekly: null,
      },
      outreachIntervalSummaryLoading: false,
      outreachSummary: null,
    }) as State,
});

export default useAnalyticsStore;
